import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import { Close as CloseIcon, Map as MapIcon, MyLocation as MyLocationIcon, Info as InfoIcon } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useUser } from '../../contexts/UserContext';
import ContactDetailModal from '../ContactDetailModal';
import { obfuscateEmail } from '../../utils/roleUtils';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to handle map centering and popup opening
const MapController: React.FC<{
  centerOnLocation?: { lat: number; lng: number; popupRef?: any };
  resetZoom?: boolean;
  mapBounds?: { center: [number, number]; zoom: number };
}> = ({ centerOnLocation, resetZoom, mapBounds }) => {
  const map = useMap();

  useEffect(() => {
    if (centerOnLocation) {
      map.setView([centerOnLocation.lat, centerOnLocation.lng], 6);
      if (centerOnLocation.popupRef) {
        setTimeout(() => {
          centerOnLocation.popupRef?.openPopup();
        }, 500);
      }
    }
  }, [centerOnLocation, map]);

  useEffect(() => {
    if (resetZoom && mapBounds) {
      map.setView(mapBounds.center, mapBounds.zoom);
    }
  }, [resetZoom, map, mapBounds]);

  return null;
};

// Component for popup with reverse geocoding
const MapPopup: React.FC<{
  item: any;
  onEmailClick: (contact: any) => void;
  getLocationFromCoordinates: (lat: number, lng: number) => Promise<{ city: string; state: string; country: string }>;
  isViewerRole: boolean;
}> = ({ item, onEmailClick, getLocationFromCoordinates, isViewerRole }) => {
  const [locationData, setLocationData] = useState<{ city: string; state: string; country: string }>({ city: '', state: '', country: '' });
  const [loadingLocation, setLoadingLocation] = useState(false);

  useEffect(() => {
    if (item.latitude && item.longitude) {
      setLoadingLocation(true);
      getLocationFromCoordinates(item.latitude, item.longitude)
        .then(location => {
          setLocationData(location);
          setLoadingLocation(false);
        })
        .catch(() => {
          setLocationData({ city: 'Unknown Location', state: '', country: '' });
          setLoadingLocation(false);
        });
    }
  }, [item.latitude, item.longitude, getLocationFromCoordinates]);

  const formatLocation = () => {
    if (loadingLocation) return 'Loading location...';
    
    const { city, state, country } = locationData;
    const parts = [city];
    
    if (state) parts.push(state);
    if (country) parts.push(country);
    
    return parts.join(', ');
  };

  return (
    <Box sx={{ minWidth: '200px', maxWidth: '300px' }}>
      {item.contacts.length > 0 && (
        <Box>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            {formatLocation()}
          </Typography>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Contacts ({item.contacts.length}):
          </Typography>
          <Box
            sx={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 1,
              backgroundColor: '#f8f9fa'
            }}
          >
            {item.contacts.map((contact: any, contactIndex: number) => (
              <Box
                key={contactIndex}
                sx={{
                  py: 0.1,
                  borderBottom: contactIndex < item.contacts.length - 1 ? '1px solid #e0e0e0' : 'none'
                }}
              >
                <Typography 
                  variant="body2" 
                  fontWeight="medium" 
                  sx={{ 
                    m: 0, 
                    p: 0, 
                    display: 'block',
                    lineHeight: 1.2
                  }}
                >
                  {contact.first_name} {contact.last_name}
                </Typography>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{
                    m: 0,
                    p: 0,
                    display: 'block',
                    lineHeight: 1.1,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': {
                      color: 'primary.dark'
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEmailClick(contact);
                  }}
                >
                  {isViewerRole ? obfuscateEmail(contact.email_address) : contact.email_address}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface GeographicMapModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  clientId: number;
  // Props for using existing data instead of fetching
  mapData?: any[];
  loading?: boolean;
  error?: string | null;
}

interface MapData {
  country: string;
  state?: string;
  city?: string;
  count: number;
  percentage: number;
  latitude?: number;
  longitude?: number;
  contacts: {
    first_name: string;
    last_name: string;
    email_address: string;
  }[];
}

const GeographicMapModal: React.FC<GeographicMapModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  clientId,
  mapData: propMapData,
  loading: propLoading,
  error: propError
}) => {
  const [mapData, setMapData] = useState<MapData[]>([]);
  const [loading, setLoading] = useState(propLoading || false);
  const [error, setError] = useState<string | null>(propError || null);
  const [centerOnLocation, setCenterOnLocation] = useState<{ lat: number; lng: number; popupRef?: any } | undefined>();
  const popupRefs = useRef<Map<number, any>>(new Map());
  const [highlightedContact, setHighlightedContact] = useState<string | null>(null);
  const [selectedContactForDetail, setSelectedContactForDetail] = useState<any>(null);
  const [resetZoom, setResetZoom] = useState(false);
  const [locationCache, setLocationCache] = useState<Map<string, { city: string; state: string; country: string }>>(new Map());
  const { selectedClientId, isICRole } = useUser();

  const effectiveClientId = clientId || selectedClientId;

  // Calculate map bounds to fit all pins
  const calculateMapBounds = useCallback((data: MapData[]) => {
    if (!data || data.length === 0) {
      return { center: [39.8283, -98.5795], zoom: 4 }; // Default to USA
    }

    const validCoords = data.filter(item => 
      item.latitude !== null && 
      item.latitude !== undefined &&
      item.longitude !== null && 
      item.longitude !== undefined &&
      !isNaN(item.latitude) && 
      !isNaN(item.longitude)
    );

    if (validCoords.length === 0) {
      return { center: [39.8283, -98.5795], zoom: 4 }; // Default to USA
    }

    if (validCoords.length === 1) {
      return { center: [validCoords[0].latitude!, validCoords[0].longitude!], zoom: 8 };
    }

    // Calculate bounds
    const lats = validCoords.map(item => item.latitude!);
    const lngs = validCoords.map(item => item.longitude!);
    
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    // Calculate center
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    
    // Calculate appropriate zoom level based on bounds
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);
    
    let zoom = 4;
    if (maxDiff > 50) zoom = 2;
    else if (maxDiff > 20) zoom = 3;
    else if (maxDiff > 10) zoom = 4;
    else if (maxDiff > 5) zoom = 5;
    else if (maxDiff > 2) zoom = 6;
    else if (maxDiff > 1) zoom = 7;
    else if (maxDiff > 0.5) zoom = 8;
    else zoom = 9;
    
    return { center: [centerLat, centerLng] as [number, number], zoom };
  }, []);

  const [mapBounds, setMapBounds] = useState<{ center: [number, number]; zoom: number } | null>({ center: [39.8283, -98.5795], zoom: 4 });

  // Create flattened contacts list sorted by email address
  const allContacts = mapData.flatMap((location, locationIndex) =>
    location.contacts.map((contact, contactIndex) => ({
      ...contact,
      locationIndex,
      contactIndex,
      location: location,
      uniqueKey: `${locationIndex}-${contactIndex}`
    }))
  ).sort((a, b) => a.email_address.localeCompare(b.email_address));

  // Stable callback for setting popup refs
  const setPopupRef = useCallback((index: number, ref: any) => {
    if (ref) {
      popupRefs.current.set(index, ref);
    }
  }, []);

  // Handle contact click to center map and open popup
  const handleContactClick = useCallback((contact: any) => {
    const location = contact.location;
    if (location.latitude && location.longitude) {
      const popupRef = popupRefs.current.get(contact.locationIndex);
      setCenterOnLocation({
        lat: location.latitude,
        lng: location.longitude,
        popupRef: popupRef
      });
    }
  }, []);

  // Handle pin click to scroll to contact in table
  const handlePinClick = useCallback((locationIndex: number) => {
    // Find the first contact at this location
    const contactAtLocation = allContacts.find(contact => contact.locationIndex === locationIndex);
    if (contactAtLocation) {
      setHighlightedContact(contactAtLocation.uniqueKey);
      
      // Scroll to the contact in the table
      setTimeout(() => {
        const element = document.getElementById(`contact-${contactAtLocation.uniqueKey}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      // Clear highlight after 3 seconds
      setTimeout(() => {
        setHighlightedContact(null);
      }, 3000);
    }
  }, [allContacts]);

  // Handle email click to open contact detail modal
  const handleEmailClick = useCallback((contact: any) => {
    setSelectedContactForDetail({
      id: contact.id, // Now using the correct contact ID from API
      email_address: contact.email_address,
      first_name: contact.first_name,
      last_name: contact.last_name
    });
  }, []);

  // Handle closing contact detail modal
  const handleCloseContactDetail = useCallback(() => {
    setSelectedContactForDetail(null);
  }, []);

  // Handle reset zoom
  const handleResetZoom = useCallback(() => {
    setResetZoom(true);
    setTimeout(() => setResetZoom(false), 100);
  }, []);

  // Reverse geocoding function to get city, state, and country from lat/lng
  const getLocationFromCoordinates = useCallback(async (lat: number, lng: number): Promise<{ city: string; state: string; country: string }> => {
    const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
    
    // Check cache first
    if (locationCache.has(cacheKey)) {
      return locationCache.get(cacheKey)!;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'MarketingPortal/1.0'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Reverse geocoding failed');
      }
      
      const data = await response.json();
      const address = data.address || {};
      
      const city = address.city || 
                   address.town || 
                   address.village || 
                   address.hamlet ||
                   address.suburb ||
                   'Unknown City';
      
      const state = address.state || 
                    address.province || 
                    address.region ||
                    '';
      
      const country = address.country || 
                      address.country_code?.toUpperCase() ||
                      '';
      
      const locationData = { city, state, country };
      
      // Cache the result
      setLocationCache(prev => new Map(prev.set(cacheKey, locationData)));
      
      return locationData;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return { city: 'Unknown Location', state: '', country: '' };
    }
  }, [locationCache]);

  useEffect(() => {
    if (open) {
      // Always use passed data - never fetch
      if (propMapData !== undefined) {
        setMapData(propMapData);
        const bounds = calculateMapBounds(propMapData);
        setMapBounds(bounds as { center: [number, number]; zoom: number });
      } else {
        // No data provided - clear the map
        setMapData([]);
        setMapBounds(null);
      }
    }
  }, [open, propMapData]);

  // Update local state when props change
  useEffect(() => {
    if (propLoading !== undefined) {
      setLoading(propLoading);
    }
  }, [propLoading]);

  useEffect(() => {
    if (propError !== undefined) {
      setError(propError);
    }
  }, [propError]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <MapIcon color="primary" />
            <Box>
              <Typography variant="h6">{title}</Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: '600px' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : mapData.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
            <Typography variant="body1" color="text.secondary">
              No geographic data available for the selected criteria.
            </Typography>
          </Box>
        ) : (
          <Box display="flex" sx={{ height: '100%' }}>
            {/* Left side - Map (2/3) */}
            <Box sx={{ width: '66.67%', height: '100%', borderRight: '1px solid #e0e0e0', p: 1 }}>
              <MapContainer
                center={mapBounds?.center || [39.8283, -98.5795]}
                zoom={mapBounds?.zoom || 4}
                style={{ height: '100%', width: '100%' }}
              >
                <MapController centerOnLocation={centerOnLocation} resetZoom={resetZoom} mapBounds={mapBounds || undefined} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {mapData
                  .filter(item => item.latitude && item.longitude)
                  .map((item, index) => (
                    <Marker
                      key={index}
                      position={[item.latitude!, item.longitude!]}
                      ref={(ref) => setPopupRef(index, ref)}
                      eventHandlers={{
                        click: () => handlePinClick(index)
                      }}
                    >
                      <Popup>
                        <MapPopup
                          item={item}
                          onEmailClick={handleEmailClick}
                          getLocationFromCoordinates={getLocationFromCoordinates}
                          isViewerRole={isICRole()}
                        />
                      </Popup>
                    </Marker>
                  ))}
              </MapContainer>
            </Box>

            {/* Right side - Contact Table (1/3) */}
            <Box sx={{ width: '33.33%', height: '100%', borderLeft: '1px solid #e0e0e0' }}>
              <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h6">
                    Contacts ({allContacts.length})
                  </Typography>
                  <Tooltip title="Only includes contacts with geographic data (latitude/longitude). Some contacts may be excluded if their location data is unavailable." arrow>
                    <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allContacts.map((contact) => (
                        <TableRow
                          key={contact.uniqueKey}
                          id={`contact-${contact.uniqueKey}`}
                          hover
                          onClick={() => handleContactClick(contact)}
                          sx={{ 
                            cursor: 'pointer',
                            backgroundColor: highlightedContact === contact.uniqueKey ? '#e3f2fd' : 'inherit',
                            transition: 'background-color 0.3s ease'
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2">
                              {contact.first_name.trim()} {contact.last_name.trim()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {isICRole() ? obfuscateEmail(contact.email_address) : contact.email_address}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Tooltip title="Reset zoom to show all locations">
          <IconButton onClick={handleResetZoom} size="small">
            <MyLocationIcon />
          </IconButton>
        </Tooltip>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>

      {/* Contact Detail Modal */}
      {selectedContactForDetail && (
        <ContactDetailModal
          open={!!selectedContactForDetail}
          onClose={handleCloseContactDetail}
          contactId={selectedContactForDetail?.id || 0}
          clientId={effectiveClientId || 0}
        />
      )}
    </Dialog>
  );
};

export default GeographicMapModal;